import {
  resolveKind,
  type FieldDescriptor,
  type FieldRole,
  type KindDef,
  type ListViewId,
  type DetailViewId,
} from '@gm-binder/shared';

/**
 * View registry metadata (plan §3 "View registry"). The actual generic renderers live in
 * components/views (GenericList / GenericDetail); bespoke views reuse existing rich section
 * components at runtime. These option tables drive the kind-builder's list/detail pickers.
 */

/** Resolve a kind def, falling back to a minimal generic kind when the id is unknown. */
export function resolveKindDef(
  kindId: string,
  customKinds?: Record<string, Partial<KindDef>>,
): KindDef {
  const def = resolveKind(kindId, customKinds);
  if (def) return def;
  return {
    id: kindId,
    label: kindId,
    icon: '📄',
    scope: 'adventure',
    fields: [{ key: 'name', type: 'text', label: 'Nome', role: 'title' }],
    views: { list: 'cards', detail: 'profile' },
    storageDir: kindId,
  };
}

export interface ViewOption {
  id: string;
  label: string;
  hint: string;
}

/** Generic list visualizations a custom kind may choose. */
export const LIST_VIEW_OPTIONS: ViewOption[] = [
  { id: 'cards', label: 'Schede', hint: 'Griglia di schede con icona, titolo e sottotitolo' },
  { id: 'grid', label: 'Griglia', hint: 'Griglia compatta di carte' },
  { id: 'list', label: 'Elenco', hint: 'Righe compatte' },
  { id: 'table', label: 'Tabella', hint: 'Tabella dei campi scalari' },
  { id: 'gallery', label: 'Galleria', hint: 'Griglia di immagini' },
  { id: 'journal', label: 'Diario', hint: 'Voci in stile diario/lettera' },
  { id: 'statblockCards', label: 'Schede statistiche', hint: 'Carte con blocco statistiche' },
  { id: 'mapGrouped', label: 'Raggruppato', hint: 'Raggruppa per un campo (es. piano/area)' },
];

/** Generic detail visualizations a custom kind may choose. */
export const DETAIL_VIEW_OPTIONS: ViewOption[] = [
  { id: 'profile', label: 'Profilo', hint: 'Intestazione + campi + corpo markdown' },
  { id: 'page', label: 'Pagina', hint: 'Documento markdown a tutta larghezza' },
];

const GENERIC_LIST = new Set(LIST_VIEW_OPTIONS.map((o) => o.id));
const GENERIC_DETAIL = new Set(DETAIL_VIEW_OPTIONS.map((o) => o.id));

/** True when a list view id is rendered by the generic templates (not a bespoke runtime view). */
export function isGenericList(id: string): id is ListViewId {
  return GENERIC_LIST.has(id);
}

/** True when a detail view id is rendered by the generic templates. */
export function isGenericDetail(id: string): id is DetailViewId {
  return GENERIC_DETAIL.has(id);
}

/** First field carrying the given role. */
export function roleField(kind: KindDef, role: FieldRole): FieldDescriptor | undefined {
  return kind.fields.find((f) => f.role === role);
}

/** Read an entity's display title (role:title field, else name/title/id). */
export function entityTitle(kind: KindDef, entity: Record<string, unknown>): string {
  const key = roleField(kind, 'title')?.key;
  for (const k of [key, 'name', 'title', 'id'].filter(Boolean) as string[]) {
    const v = entity[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return String(entity.id ?? '');
}

/** Read an entity's icon (role:icon field value, else the kind icon). */
export function entityIcon(kind: KindDef, entity: Record<string, unknown>): string {
  const key = roleField(kind, 'icon')?.key;
  const v = key ? entity[key] : undefined;
  return (typeof v === 'string' && v) || kind.icon || '📄';
}

/** Fields that should be shown as labeled scalar values in a generic detail/table. */
export function scalarFields(kind: KindDef): FieldDescriptor[] {
  return kind.fields.filter(
    (f) =>
      !['markdown', 'statblock', 'rows', 'image', 'tags', 'refCount'].includes(f.type) &&
      f.role !== 'title' &&
      f.role !== 'icon',
  );
}

/** Format a scalar field value for display. */
export function formatScalar(value: unknown): string {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) {
    return value
      .map((v) => (v && typeof v === 'object' && 'ref' in v ? String((v as { ref: string }).ref) : String(v)))
      .join(', ');
  }
  if (typeof value === 'boolean') return value ? 'Sì' : 'No';
  return String(value);
}
